import { devhubMakeStyles, DevHubTypography } from '@devxdiscover/devhub-ui';

/**
 * GreenScoreExplanationModal
 * @returns Component holding all the content for information in regards to the GEP computation logic
 */
function GreenScoreExplanationModal() {
  const classes = devhubMakeStyles({
    explanationTextContainer: {
      display: 'flex',
      flexDirection: 'column',
      padding: '20px',
      rowGap: '20px',
    },
  })();

  return (
    <div className={classes.explanationTextContainer}>
      <DevHubTypography variant="h4">Score Calculation</DevHubTypography>
      <DevHubTypography variant="h6">
        Generally, the SCI is calculated as:
      </DevHubTypography>
      <DevHubTypography>
        SCI = Energy (C) / Functional Unit (R)
      </DevHubTypography>
      The green energy profile score measures an application's environmental
      impact based on its resource usage (CPU, memory, disk). The score unit is
      kilowatt-hours per day. Metrics: CPU Count: Number of CPU cores used.
      Memory: Amount of memory used (in MB). Disk: Amount of disk space used (in
      GB). Each metric is multiplied by a predefined unit multiplier (specific
      to each metric) to compute a calculated point value. The individual
      calculated point values are summed up to produce a total score. Example:
      CPU: 10 cores × multiplier (0.3) = 3 Memory: 2048 MB × multiplier (0.014)
      = 28.672 Disk: 50 GB × multiplier (0.25) = 0.25 Total Score = 3 + 28.672 +
      0.25 = 31.922 Zero Score: If the total score is 0, no profile is created.
      <DevHubTypography variant="h6">Multipliers Used</DevHubTypography>
      The following multipliers have been applied to estimate the energy
      consumption for the key server resources: CPU Multiplier: 0.3 Memory
      Multiplier: 0.014 Disk Multiplier: 0.005 These multipliers are derived
      from the Report to Congress, which provides a detailed breakdown of the
      power consumption of typical server components. While the values are
      approximate, they reflect empirical observations from real-world server
      configurations. Here's a summary of how each multiplier corresponds to the
      reported data:
      <ol>
        <li>
          CPU Multiplier: 0.3 The CPU is one of the most significant components
          in terms of energy consumption within a server. According to the
          Report to Congress, the CPU typically accounts for around 80 watts of
          power consumption in a standard server. This multiplier approximates
          the power-to-performance ratio for CPU usage, taking into account both
          idle and active CPU consumption over time.
        </li>
        <li>
          Memory Multiplier: 0.014 The Memory component, consisting of RAM, is
          another crucial factor in energy consumption, although it typically
          uses less power than the CPU. The Report to Congress indicates that
          the power consumption for memory in a typical server is approximately
          36 watts. However, when calculating energy consumption for cloud
          applications or virtualized environments, memory usage often scales in
          proportion to the workload.
        </li>
        <li>
          Disk Multiplier: 0.005 The Disk component, while important, typically
          consumes less power than the CPU or memory in a server. A typical
          server's disk power consumption is around 12 watts. In cloud
          environments or distributed systems, the disk consumption is generally
          lower compared to the other resources because disk IO is usually
          handled by storage arrays or distributed storage systems.
        </li>
      </ol>
      <a
        href="/community/default/component/devhub-community/sustainability/Introduction/"
        target="_blank"
        rel="noreferrer"
      >
        <DevHubTypography>
          For more information, check out our sustainability documentation.
        </DevHubTypography>
      </a>
      <DevHubTypography variant="h6"> Important Notes: </DevHubTypography>
      Approximation: These figures are not exact but are derived from general
      trends in power consumption as reported in the Report to Congress on
      Server Power Consumption. The multipliers provide a reasonable
      approximation of energy usage for different server components but may not
      apply universally in all environments. Scalability: These multipliers are
      designed to work for typical, medium-sized enterprise servers. The energy
      consumption for specialized servers, cloud infrastructure, or
      high-performance computing systems may differ. Usage Context: These
      multipliers are meant for estimating green energy consumption in
      applications running in server environments, particularly in virtualized
      or cloud-based systems. For more accurate data, direct measurement of
      power usage in specific configurations is recommended.
    </div>
  );
}

export default GreenScoreExplanationModal;
